import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ListTodo, Loader2, Plus } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  deleteTask, getProjects, getTasks, setTaskStatus,
  type Project, type Task, type TaskStatus,
} from "../../../lib/projects";
import { Btn, EmptyState, PageHead, listCard } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { TASK_STATUSES } from "../projects/status";
import { NewTaskModal } from "./NewTaskModal";
import { PRIORITIES } from "./priority";
import { TaskBoard } from "./TaskBoard";
import { COLS, TaskRow } from "./TasksListRow";
import {
  ALL, DEFAULT_TASK_FILTERS, TasksToolbar, VIEWS, type TaskFilters, type View,
} from "./TasksToolbar";

/** The design's page size for a list. The board is never paged: a column that
 *  stops at row 25 is a column that lies. 500 is the endpoint's ceiling (see
 *  `listQuery` in tasks.routes.ts). */
const PAGE_SIZE = 25;
const BOARD_SIZE = 500;

/** Everything that decides which tasks are on screen, and in what shape: the
 *  toolbar's filters, plus the two things the toolbar does not own. `q` here is
 *  the COMMITTED search - the typing buffer stays in the component. */
interface ListView extends TaskFilters {
  view: View;
  page: number;
}

/** No question asked yet: the whole register, as a list, from the top. The
 *  filter half is taken from the toolbar's own defaults rather than restated, so
 *  a filter added there cannot end up with two different "no opinion" values. */
const DEFAULTS: ListView = { ...DEFAULT_TASK_FILTERS, view: "list", page: 1 };

/**
 * The address bar, read.
 *
 * Every field falls back to its default instead of throwing. These values do not
 * come from this screen alone - they come from a bookmark, a pasted link, a
 * message sent last month - so `?status=blocked` from a version that had that
 * status, or `?page=` from a truncated copy-paste, has to draw the register
 * rather than a blank tab.
 */
function readView(sp: URLSearchParams): ListView {
  const page = Number.parseInt(sp.get("page") ?? "", 10);
  return {
    q: (sp.get("q") ?? "").trim(),
    // `find` over the same arrays the controls are built from, so a value is
    // legal here exactly when the toolbar can show it.
    status: TASK_STATUSES.find((s) => s === sp.get("status")) ?? DEFAULTS.status,
    priority: PRIORITIES.find((p) => p === sp.get("priority")) ?? DEFAULTS.priority,
    // No `find` for these two: their legal set is a database table, not a
    // constant, and it arrives after the first render. Checking an id against a
    // list that starts empty would erase a perfectly good id out of a pasted
    // link before the options had loaded. An id nobody has is answered by the
    // endpoint with an empty set, which is the truthful answer to the question.
    projectId: sp.get("projectId") || DEFAULTS.projectId,
    assignee: sp.get("assignee") || DEFAULTS.assignee,
    // Nor for the tag: the vocabulary is a column of free text that arrives
    // after the first render, and a tag the menu has not heard of yet is still a
    // perfectly good question to ask the server.
    tag: sp.get("tag") || DEFAULTS.tag,
    view: VIEWS.find((v) => v === sp.get("view")) ?? DEFAULTS.view,
    page: Number.isFinite(page) && page >= 1 ? page : DEFAULTS.page,
  };
}

/**
 * The address bar, written.
 *
 * Built fresh rather than patched, and anything sitting at its default is left
 * out: an unfiltered register should look like `/tasks`, because a URL reading
 * `?q=&status=all&priority=all&projectId=all&assignee=all&view=list&page=1` is
 * one nobody can tell has a filter on it at a glance. Nothing else on this route
 * carries a parameter, so there is none to preserve.
 */
function writeView(v: ListView): URLSearchParams {
  const sp = new URLSearchParams();
  if (v.q) sp.set("q", v.q);
  if (v.status !== DEFAULTS.status) sp.set("status", v.status);
  if (v.priority !== DEFAULTS.priority) sp.set("priority", v.priority);
  if (v.projectId !== DEFAULTS.projectId) sp.set("projectId", v.projectId);
  if (v.assignee !== DEFAULTS.assignee) sp.set("assignee", v.assignee);
  if (v.tag !== DEFAULTS.tag) sp.set("tag", v.tag);
  if (v.view !== DEFAULTS.view) sp.set("view", v.view);
  if (v.page !== DEFAULTS.page) sp.set("page", String(v.page));
  return sp;
}

/**
 * The task register.
 *
 * Two views over one query, the same shape the project list uses. The list is
 * the register - everything in order, with the deadline on it. The board is the
 * same records grouped by status, which is a board because that is the shape of
 * the question ("what is moving, what is stuck"), not because a board looks
 * busier.
 *
 * There is no KPI strip here, unlike Sales. The figures worth showing would be
 * counts per status, and the board already writes those on its columns; a strip
 * that could only be honest in one of the two views is worse than none. Nothing
 * on this screen is derived from a number the endpoint did not return.
 *
 * What is being asked for lives in the URL rather than in component state, for
 * the same reasons it does on the project list: a reload - after a deploy, after
 * opening a task in the same tab and coming back - used to drop the operator
 * into an unfiltered register on page one, and "the four overdue ones on Mari's
 * plate" was a thing you could see but not send to anybody.
 *
 * What is left in this file is the query and the page: the filter row is
 * `TasksToolbar`, the row is `TasksListRow`. Both were lifted out whole when the
 * register grew an assignee column, before the additions rather than after.
 */
export function TasksList() {
  const t = useT();
  const [sp, setSp] = useSearchParams();
  const { q, status, priority, projectId, assignee, tag, view, page } = readView(sp);
  const [rows, setRows] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  // The search box, which trails the committed query by 300ms.
  const [qInput, setQInput] = useState(q);
  const [total, setTotal] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [confirmDel, setConfirmDel] = useState<Task | null>(null);

  /** One change to what the register is showing, written into the address bar. */
  const patchView = useCallback(
    (change: Partial<ListView>, replace = false) =>
      setSp((prev) => writeView({ ...readView(prev), ...change }), { replace }),
    [setSp],
  );

  // The term this screen last committed. A query that differs from it arrived
  // from somewhere else - the back button, a link somebody opened - and belongs
  // in the box, rather than being typed over by a buffer holding the old word.
  const committed = useRef(q);
  useEffect(() => {
    if (q === committed.current) return;
    committed.current = q;
    setQInput(q);
  }, [q]);

  // The typing buffer becomes the query once the operator stops. `replace`,
  // because one history entry per keystroke turns the back button into a
  // machine that spells the search term backwards one letter at a time.
  useEffect(() => {
    const next = qInput.trim();
    if (next === q) return;
    const id = setTimeout(() => {
      committed.current = next;
      patchView({ q: next, page: 1 }, true);
    }, 300);
    return () => clearTimeout(id);
  }, [qInput, q, patchView]);

  // The toolbar hands back the whole filter object, so the two halves are told
  // apart here: the search box is a buffer the debounce above commits, the
  // menus go straight to the address bar. A menu that did not move writes
  // nothing, or every keystroke would push a history entry through this path as
  // well as through the debounce.
  //
  // A new question is asked from the top. The page reset travels in the SAME
  // write as the filter, so no render sees a new filter beside an old page
  // number - and, unlike the effect this replaced, nothing resets the page
  // afterwards, which is what would throw away a page that arrived in the URL
  // on purpose.
  const onFilters = useCallback(
    (next: TaskFilters) => {
      setQInput(next.q);
      const change: Partial<ListView> = {};
      if (next.status !== status) change.status = next.status;
      if (next.priority !== priority) change.priority = next.priority;
      if (next.projectId !== projectId) change.projectId = next.projectId;
      if (next.assignee !== assignee) change.assignee = next.assignee;
      if (next.tag !== tag) change.tag = next.tag;
      if (Object.keys(change).length > 0) patchView({ ...change, page: 1 });
    },
    [status, priority, projectId, assignee, tag, patchView],
  );

  const onView = useCallback((next: View) => patchView({ view: next, page: 1 }), [patchView]);
  const goPage = useCallback((next: number) => patchView({ page: Math.max(1, next) }), [patchView]);

  // Every filter is answered by the endpoint. The search used to be applied to
  // the loaded array, which silently meant "of the first five hundred" and left
  // the count above it describing a different set from the rows below it.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTasks({
        page: view === "list" ? page : 1,
        pageSize: view === "list" ? PAGE_SIZE : BOARD_SIZE,
        q: q || undefined,
        // The board shows every status as a column of its own, so the status
        // filter only applies to the list.
        status: view === "list" && status !== ALL ? status : undefined,
        priority: priority === ALL ? undefined : priority,
        projectId: projectId === ALL ? undefined : projectId,
        assignee: assignee === ALL ? undefined : assignee,
        // The empty string is this filter's "no opinion", not `ALL` - a tag can
        // legitimately be the word "all". See `TaskFilters.tag`.
        tag: tag || undefined,
      });
      // A page number can now arrive from outside and point past the end of a
      // set that has shrunk since - a link sent last week, a bookmark kept for a
      // month, and this register is the one people bookmark filtered. An empty
      // card under a filter reads as "the filter found nothing", so walk back to
      // the start of what does exist instead of drawing it. The guard cannot
      // loop: it only fires above page 1 and it lands on page 1.
      if (res.rows.length === 0 && res.total > 0 && page > 1) {
        patchView({ page: 1 }, true);
        return;
      }
      setRows(res.rows);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [q, status, priority, projectId, assignee, tag, view, page, patchView]);

  useEffect(() => {
    void load();
  }, [load]);

  // The project filter's options. A failure here leaves the filter at "all
  // projects", which is the state it starts in anyway - the task list is still
  // usable without it, so it does not take the screen down with it.
  //
  // `archived: "all"`, and the two consumers of this list want opposite things
  // out of it - see `liveProjects` below.
  //
  // The FILTER needs the archived ones. Nothing in `GET /api/admin/crm/tasks`
  // narrows a task by its project's archive state (the route says so where the
  // tag filter is built), so the tasks of an archived project are still on this
  // page. Take the endpoint's default and the menu stops offering a project whose
  // tasks are visibly listed underneath it - and worse, a URL that already
  // carries `?projectId=` for one lands on a `MenuField` that cannot find the
  // value and falls back to its first option: the list is filtered to one project
  // while the control reads "all projects", which is a filter that is on and
  // reads as off.
  useEffect(() => {
    getProjects({ pageSize: 100, archived: "all" })
      .then((r) => setProjects(r.rows))
      .catch(() => {});
  }, []);

  // What may be given NEW work. The archive's whole promise is that a retired
  // project stops being OFFERED, so the create form gets the live ones only -
  // the opposite half of the same list the filter above needs whole.
  const liveProjects = useMemo(() => projects.filter((p) => !p.archivedAt), [projects]);

  async function move(id: string, s: TaskStatus) {
    // Optimistic: the card has to land in the new column under the cursor, not
    // after a round trip. A failed write reloads rather than guessing.
    setRows((prev) => prev.map((x) => (x.id === id ? { ...x, status: s } : x)));
    try {
      await setTaskStatus(id, s);
    } catch {
      void load();
    }
  }

  async function remove(task: Task) {
    await deleteTask(task.id);
    setRows((prev) => prev.filter((x) => x.id !== task.id));
    setTotal((n) => Math.max(0, n - 1));
    setConfirmDel(null);
  }

  return (
    // One open menu at a time across every row - and the toolbar's filters are
    // `Pop` menus too, so the host has to sit above both.
    <PopHost>
      <PageHead
        title={t("tasks.title")}
        sub={t("tasks.count", { n: total })}
        actions={
          <Btn tone="brand" onClick={() => setShowNew(true)}>
            <Plus className="size-4" />
            {t("tasks.add")}
          </Btn>
        }
      />

      <TasksToolbar
        filters={{ q: qInput, status, priority, projectId, assignee, tag }}
        onFilters={onFilters}
        projects={projects}
        view={view}
        onView={onView}
      />

      {loading ? (
        <div className="crm-card flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : view === "board" ? (
        <TaskBoard tasks={rows} onMove={move} onDelete={setConfirmDel} />
      ) : (
        <div className="crm-card" style={listCard(COLS)}>
          {rows.length === 0 ? (
            <EmptyState
              icon={ListTodo}
              title={t("tasks.empty")}
              body={t("tasks.emptyBody")}
              action={
                <Btn tone="brand" onClick={() => setShowNew(true)}>
                  <Plus className="size-4" />
                  {t("tasks.add")}
                </Btn>
              }
            />
          ) : (
            <>
              <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
                <span>{t("tasks.titleField")}</span>
                <span>{t("projects.statusCol")}</span>
                <span>{t("tasks.priority")}</span>
                <span>{t("projects.assignees")}</span>
                <span>{t("tasks.dueDate")}</span>
                <span>{t("tasks.comments")}</span>
                <span />
              </div>

              <div className="crm-rows pt-0">
                {rows.map((tk) => (
                  <TaskRow
                    key={tk.id}
                    task={tk}
                    onMove={(s) => void move(tk.id, s)}
                    onDelete={() => setConfirmDel(tk)}
                  />
                ))}
              </div>

              <div className="crm-tfoot">
                {t("tasks.showing", {
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: (page - 1) * PAGE_SIZE + rows.length,
                  total,
                })}
                <span className="crm-push flex gap-2.5">
                  <Btn small disabled={page <= 1} onClick={() => goPage(page - 1)}>
                    <ChevronLeft className="size-4" />
                    {t("common.prev")}
                  </Btn>
                  <Btn small disabled={page * PAGE_SIZE >= total} onClick={() => goPage(page + 1)}>
                    {t("common.next")}
                    <ChevronRight className="size-4" />
                  </Btn>
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {showNew ? (
        <NewTaskModal
          // The live ones only. The form's project control is a native `select`,
          // which shows NOTHING for a value that is not among its options while
          // the state behind it still holds that value - so an archived project
          // handed in here would be submitted with the field looking empty, which
          // is precisely the accident the archive exists to prevent.
          projects={liveProjects}
          // For the same reason the filter's project is only carried into the
          // form when it is still a project you may book work against. Filtering
          // the task list by an archived project is a legitimate thing to do;
          // having "New task" silently inherit it is not.
          defaultProjectId={
            projectId !== ALL && liveProjects.some((p) => p.id === projectId) ? projectId : undefined
          }
          onClose={() => setShowNew(false)}
          onCreated={() => void load()}
        />
      ) : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("tasks.deleteTitle")}
          body={t("tasks.deleteConfirm")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </PopHost>
  );
}
